import type { FamilyPublishedPriceItem } from '../rate-share/family-types'
// ==================== 通用响应类型 ====================
import type { PriceFclMatchResultVO } from '@api/sass/rate-management/types'
import type { BusinessIncreaseRuleAirPrice, BusinessIncreaseRulesVO } from '@/api/sass/business-increase-rules/types'

export interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
  success: boolean
}

export interface PageResponse<T = any> {
  records: T[]
  total: number
  size: number
  current: number
  pages: number
}

// ==================== 共享业务类型 ====================
export interface PriceChargingItemDTO {
  id?: string
  chargeItemId?: string | number
  isBasicFee?: boolean | number // 是否是基础费用：1-是，0-否
  chargeName?: string
  chargeItemName?: string
  type: number // 1: 按箱型计费, 2: 非按箱型计费
  currency: string
  remarks?: string

  // 按箱型计费字段
  gp20?: number
  gp40?: number
  hc40?: number
  hc45?: number
  nor40?: number
  nor20?: number
  hc20?: number
  tk20?: number
  tk40?: number
  ot20?: number
  ot40?: number
  fr20?: number
  fr40?: number

  // 非按箱型计费字段
  measure?: string // 计量单位
  unitRate?: number // 单价
  quotedUnitRate?: number // 报价单价
  quantity?: number // 数量
  hq40?: number
  hq45?: number
  rf20?: number
  rf40?: number

  gp20QuotedPrice?: number
  gp40QuotedPrice?: number
  hc20QuotedPrice?: number
  hc40QuotedPrice?: number
  hc45QuotedPrice?: number
  nor20QuotedPrice?: number
  nor40QuotedPrice?: number
  tk20QuotedPrice?: number
  tk40QuotedPrice?: number
  ot20QuotedPrice?: number
  ot40QuotedPrice?: number
  fr20QuotedPrice?: number
  fr40QuotedPrice?: number
  hq40QuotedPrice?: number
  hq45QuotedPrice?: number
  rf20QuotedPrice?: number
  rf40QuotedPrice?: number

  feeType?: number // 费用位置类型：1-海运附加费, 2-起运港附加费, 3-卸货港附加费
}

export interface PriceRegionDTO {
  id: string
  name: string
  code: string
  level: number
  parentId?: string
}

/** 商务加价前后的单个价格组成。 */
export interface BusinessIncreasePriceComponent {
  /** 数据库存储的原始成本价。 */
  costPrice?: number | null
  /** 商务规则计算得到的加价金额。 */
  markupAmount?: number | null
  /** 商务加价后的销售价。 */
  sellPrice?: number | null
  /** 当前价格是否应用了商务加价配置。 */
  applied?: boolean
}

/** 指定币种下的商务加价汇总价格。 */
export interface BusinessIncreaseCurrencyPrice extends BusinessIncreasePriceComponent {
  /** 费用币种代码。 */
  currency?: string
}

/** 港前基础运费条目的商务加价结果。 */
export interface BusinessIncreasePrePortItem {
  /** 原始费用条目主键。 */
  id?: string | number
  /** 计费方式：1-按箱型，2-按单价。 */
  type?: number
  /** 费用币种代码。 */
  currency?: string
  /** 按箱型组织的成本价和销售价。 */
  containerPrices?: Record<string, BusinessIncreasePriceComponent>
  /** 非按箱计费时的成本价和销售价。 */
  unitPrice?: BusinessIncreasePriceComponent | null
}

/** 港前查询命中的商务加价规则摘要。 */
export interface BusinessIncreaseMatchedRuleSummary {
  /** 商务加价规则数据主键。 */
  id?: string | number
  /** 商务加价规则业务编号。 */
  rulesId?: string
  /** 商务加价规则名称。 */
  ruleName?: string
}

/** 港前运价独立的商务加价结果。 */
export interface BusinessIncreasePrePortPrice {
  /** 当前运价命中的最优商务规则。 */
  matchedRule?: BusinessIncreaseMatchedRuleSummary | null
  /** 按箱型和币种组织的基础运费成本与销售价汇总。 */
  containerTotals?: Record<string, BusinessIncreaseCurrencyPrice[]>
  /** 各基础运费条目的商务加价明细。 */
  chargingItems?: BusinessIncreasePrePortItem[]
}

/** 尾程运价独立的商务加价结果，与港前采用相同价格结构。 */
export type BusinessIncreaseLastMilePrice = BusinessIncreasePrePortPrice

// ==================== 整箱运价查询类型 ====================
/** 租户员工代查客户时可选；客户登录始终使用登录企业。 */
export interface SalesQueryContext {
  clientId?: string
  clientLevel?: number
  ifMatchSalesRule?: boolean
}

export interface PriceFclPageRequest extends SalesQueryContext {
  pageNum: number
  pageSize: number
  conditions: FilterCondition[]
  sorts:any
}

export interface PriceFclListVO {
  /** 已保存伙伴最终价的来源；原生查询为空，报价不得据此重新读取发送方价格。 */
  rateSource?: 'PARTNER_SHARE'
  id: string
  priceNo: string
  freightType: number // 1: 合约价, 2: SPOT电商
  cargoType: number // 货物类型
  spaceStatus: number // 舱位状态
  priceTrend: number // 价格趋势
  status: number // 状态
  specialPriceStatus?: number | string | null // 特价状态：1-不允许申请，2-允许申请，3-特价
  pol: string // 起运港
  pod: string // 卸货港
  laneCode?: string // 航线代码
  weekdayStr?: string // 星期几可读字符串
  por: string // 收货地
  pdl: string // 卸货港
  directTransit: number // 是否直达 1-直达 2-中转
  pot?: string // 中转港
  pot1?: string // 中转港 (1st)
  pot2?: string // 中转港 (2nd)
  pot3?: string // 中转港 (3rd)
  vessel?: string // 船名
  voyage?: string // 航次
  etd?: string // 预计开船时间
  eta?: string // 预计到港时间
  weekdays?: number[] // 船期
  voyageDays?: number // 航程天数
  carrierId: string // 船公司
  contractNo?: string // 合同号
  nac?: string // 通知人
  detentionFreeDays?: number // 免费用箱天数
  demurrageFreeDays?: number // 免堆存天数
  beginDate?: string // 有效期开始
  endDate?: string // 有效期结束
  overweightRemarks?: string // 超重备注
  receivingInstructions?: string // 收货说明
  remarks?: string // 备注
  dateCreated?: string // 创建时间
  dateModified?: string // 更新时间
  createdBy?: string // 创建人
  modifiedBy?: string // 更新人
  surchargeFclVO?: SurchargeFclVO // 匹配的附加费，直接复用附加费详情对象

  // 按箱型计费的费用加总字段
  gp20Total: string | null
  gp40Total: string | null
  hc20Total: string | null
  hc40Total: string | null
  hc45Total: string | null
  nor20Total: string | null
  nor40Total: string | null
  tk20Total: string | null
  tk40Total: string | null
  ot20Total: string | null
  ot40Total: string | null
  fr20Total: string | null
  fr40Total: string | null

  // 多币种拼合显示字符串
  gp20CurrencyStr?: string
  gp40CurrencyStr?: string
  hc20CurrencyStr?: string
  hc40CurrencyStr?: string
  hc45CurrencyStr?: string
  nor20CurrencyStr?: string
  nor40CurrencyStr?: string
  tk20CurrencyStr?: string
  tk40CurrencyStr?: string
  ot20CurrencyStr?: string
  ot40CurrencyStr?: string
  fr20CurrencyStr?: string
  fr40CurrencyStr?: string

  matchResult?: PriceFclMatchResultVO | null // 匹配 加价规则、附加费 结果

  // 费用明细
  chargingItems?: PriceChargingItemDTO[]
}

import type { PriceFclVO as SurchargeFclVO } from '../surcharge-management/types'

export interface PriceFclVO extends PriceFclListVO {
  chargingItems: PriceChargingItemDTO[]
  containerTypeConfig?: string // 箱型配置JSON字符串
}

// ==================== 港前运价查询类型 ====================
export interface PricePrePortPageRequest extends SalesQueryContext {
  pageNum: number
  pageSize: number
  conditions: FilterCondition[]
  sorts?: any[]
}

export interface PricePrePortListVO {
  id: string
  priceNo: string
  freightType: number // 1: 合约价, 2: SPOT电商
  cargoType: number // 货物类型
  spaceStatus: number // 舱位状态
  priceTrend: number // 价格趋势
  status: number // 状态
  transportMode?: number // 运输方式: 1-拖车 2-驳船 3-铁路
  priceMethod?: number // 运价类型（历史）
  specialPriceStatus?: number | string | null // 特价状态：1-不允许申请，2-允许申请，3-特价
  pol: number // 起运港
  pod: number // 目的港
  por: number // 收货地
  pdl: number // 卸货港
  directFlag: boolean // 是否直达
  pot1?: number // 中转港 (1st)
  pot2?: number // 中转港 (2nd)
  pot3?: number // 中转港 (3rd)
  vessel?: string // 船名
  voyage?: string // 航次
  etd?: string // 预计开船时间
  eta?: string // 预计到港时间
  weekdays?: number[] // 船期
  voyageDays?: number // 航程天数
  carrierId: number // 船公司
  contractNo?: string // 合同号
  nac?: string // 通知人
  detentionFreeDays?: number // 免费用箱天数
  demurrageFreeDays?: number // 免堆存天数
  beginDate?: string // 有效期开始
  endDate?: string // 有效期结束
  overweightRemarks?: string // 超重备注
  receivingInstructions?: string // 收货说明
  remarks?: string // 备注
  dateCreated?: string // 创建时间
  dateModified?: string // 更新时间
  createdBy?: string // 创建人
  modifiedBy?: string // 更新人

  // 地址信息（已展开，不再需要regionItems）
  regionId: string // 区域ID
  addressType: number // 1-第三方地址、2-亚马逊仓库、3-易仓
  level1Id: string | null // 一级行政区ID
  level1Name: string | null // 一级行政区名称（省/州）
  level2Id: string | null // 二级行政区ID
  level2Name: string | null // 二级行政区名称（市/县）
  level3Id: string | null // 三级行政区ID
  level3Name: string | null // 三级行政区名称（区/镇）
  level4Id: string | null // 四级行政区ID
  level4Name: string | null // 四级行政区名称（街道/村）
  level5Id: string | null // 五级行政区ID
  level5Name: string | null // 五级行政区名称（社区）
  level6Id: string | null // 六级行政区ID
  level6Name: string | null // 六级行政区名称（自然村）
  detailAddress: string // 详细地址
  postalCode: string | null // 邮编
  warehouseId: number | null // 仓库ID

  // 港口信息
  portId: string // 港口ID

  // 按箱型计费的费用加总字段
  gp20Total: number | null // 20GP箱总费用
  gp40Total: number | null // 40GP箱总费用
  hc20Total: number | null // 20HC箱总费用
  hc40Total: number | null // 40HC箱总费用
  hc45Total: number | null // 45HC箱总费用
  nor20Total: number | null // 20NOR箱总费用
  nor40Total: number | null // 40NOR箱总费用
  tk20Total: number | null // 20TK箱总费用
  tk40Total: number | null // 40TK箱总费用
  ot20Total: number | null // 20OT箱总费用
  ot40Total: number | null // 40OT箱总费用
  fr20Total: number | null // 20FR箱总费用
  fr40Total: number | null // 40FR箱总费用

  // 新增字段：组合ID，用于导出时标识
  compositeId?: string // 组合ID，用于导出时标识港前运价
  surchargePrePortVOS?: Array<Record<string, unknown>>
  /** 独立于公司加价字段的港前商务价格。 */
  businessPrice?: BusinessIncreasePrePortPrice
  /** 当前客户命中销售规则后的最终查询卖价。 */
  salesPrice?: BusinessIncreasePrePortPrice

  // 多币种拼合显示字符串
  gp20CurrencyStr?: string
  gp40CurrencyStr?: string
  hc20CurrencyStr?: string
  hc40CurrencyStr?: string
  hc45CurrencyStr?: string
  nor20CurrencyStr?: string
  nor40CurrencyStr?: string
  tk20CurrencyStr?: string
  tk40CurrencyStr?: string
  ot20CurrencyStr?: string
  ot40CurrencyStr?: string
  fr20CurrencyStr?: string
  fr40CurrencyStr?: string
  /** Family手动分享冻结的完整最终价表；普通维护查询可不返回。 */
  publishedPriceItems?: FamilyPublishedPriceItem[] | null
}

export interface PricePrePortVO extends PricePrePortListVO {
  chargingItems: PriceChargingItemDTO[]
  containerTypeConfig?: string // 箱型配置JSON字符串
}

// ==================== 尾程运价查询类型 ====================
export interface PriceLastMilePageRequest extends SalesQueryContext {
  pageNum: number
  pageSize: number
  conditions: FilterCondition[]
  sorts?: any[]
  groupByPriceId?: boolean
}

export interface PriceLastMileListVO {
  id: string
  priceNo: string // 尾程运价编号
  priceMethod: number // 运价类型: 1-直拖 2-支线 3-海铁
  supplier: string // 供应商
  status: number // 状态 1-草稿、2-正常、3-过期、4-下架
  specialPriceStatus?: number | string | null // 特价状态：1-不允许申请，2-允许申请，3-特价
  beginDate?: string // 生效日期
  endDate?: string // 失效日期
  remarks?: string // 备注
  voyageDays?: number // 航程天数

  // 地址信息（已展开，不再需要regionItems）
  regionId: string // 区域ID
  addressType: number // 1-第三方地址、2-亚马逊仓库、3-易仓
  level1Id: string | null // 一级行政区ID
  level1Name: string | null // 一级行政区名称（省/州）
  level2Id: string | null // 二级行政区ID
  level2Name: string | null // 二级行政区名称（市/县）
  level3Id: string | null // 三级行政区ID
  level3Name: string | null // 三级行政区名称（区/镇）
  level4Id: string | null // 四级行政区ID
  level4Name: string | null // 四级行政区名称（街道/村）
  level5Id: string | null // 五级行政区ID
  level5Name: string | null // 五级行政区名称（社区）
  level6Id: string | null // 六级行政区ID
  level6Name: string | null // 六级行政区名称（自然村）
  detailAddress: string // 详细地址
  postalCode: string | null // 邮编
  warehouseId: number | null // 仓库ID

  // 港口信息
  portId: string // 港口ID

  // 新增字段：组合ID，用于导出时标识
  compositeId?: string // 组合ID，用于导出时标识尾程运价
  surchargeLastMileVOS?: Array<Record<string, unknown>>
  /** 独立于公司加价字段的尾程商务价格。 */
  businessPrice?: BusinessIncreaseLastMilePrice
  /** 当前客户命中销售规则后的最终查询卖价。 */
  salesPrice?: BusinessIncreaseLastMilePrice

  // 按箱型计费的费用加总字段
  gp20Total: string | null
  gp40Total: string | null
  hc20Total: string | null
  hc40Total: string | null
  hc45Total: string | null
  nor20Total: string | null
  nor40Total: string | null
  tk20Total: string | null
  tk40Total: string | null
  ot20Total: string | null
  ot40Total: string | null
  fr20Total: string | null
  fr40Total: string | null

  // 多币种拼合显示字符串
  gp20CurrencyStr?: string
  gp40CurrencyStr?: string
  hc20CurrencyStr?: string
  hc40CurrencyStr?: string
  hc45CurrencyStr?: string
  nor20CurrencyStr?: string
  nor40CurrencyStr?: string
  tk20CurrencyStr?: string
  tk40CurrencyStr?: string
  ot20CurrencyStr?: string
  ot40CurrencyStr?: string
  fr20CurrencyStr?: string
  fr40CurrencyStr?: string
  /** Family手动分享冻结的完整最终价表；普通维护查询可不返回。 */
  publishedPriceItems?: FamilyPublishedPriceItem[] | null
}

export interface PriceLastMileVO extends PriceLastMileListVO {
  chargingItems: PriceChargingItemDTO[]
  containerTypeConfig?: string // 箱型配置JSON字符串
}

// ==================== LCL拼箱查询类型 ====================
export interface PriceLclPageRequest extends SalesQueryContext {
  pageNum: number
  pageSize: number
  conditions: FilterCondition[]
  sorts?: any[]
}

export interface PriceLclListVO {
  /** 已保存伙伴最终价的来源；多条基础费以chargingItems完整保留。 */
  rateSource?: 'PARTNER_SHARE'
  id: string
  priceNo: string
  por: string
  pol: string
  pdl: string
  pod: string
  supplierId: string
  carrierId: string
  chargeUnit?: string
  chargeUnitId?: string | number
  costPrice: number
  sellPrice: number
  /** 商务加价金额。 */
  businessMarkupAmount?: number | null
  /** 查询时命中的商务加价规则。 */
  matchedBusinessRule?: BusinessIncreaseRulesVO | null
  minCharge?: number
  currency: string
  beginDate: string
  endDate: string
  status: number
  specialPriceStatus?: number | string | null // 特价状态：1-不允许申请，2-允许申请，3-特价
  remark: string
  remarks?: string
  detentionFreeDays?: number
  demurrageFreeDays?: number
  weightLimit?: string | number
  receivingInstructions?: string
  rateType?: number
  directFlag?: number | boolean
  departureSchedule?: string
  voyageDays?: number
  spaceStatus?: number
  laneCodes?: string[]
  vessel?: string
  voyage?: string
  transferPortInfo?: Array<string | number>
  // 多币种拼合显示字符串
  currencyStr?: string
  surcharges?: any[]
  /** 运价费用项（含基础费与用户创建附加费） */
  chargingItems?: any[]
}

export interface PriceLclVO extends PriceLclListVO {
  // 详情字段
}

// ==================== 空运查询类型 ====================
export interface PriceAirPageRequest extends SalesQueryContext {
  pageNum: number
  pageSize: number
  conditions: FilterCondition[]
  sorts?: any[]
}

export interface PriceAirListVO {
  id: string
  priceNo: string
  airlineId?: string
  pol?: string
  pod?: string
  supplierId?: string
  cargoType?: number | string
  currency?: string
  beginDate?: string
  endDate?: string
  status?: number
  specialPriceStatus?: number | string | null // 特价状态：1-不允许申请，2-允许申请，3-特价
  matrix?: any
  chargingItems?: any[]
  surcharges?: any[]
  remarks?: string
  currencyStr?: string
  directFlag?: number
  flightNo?: string
  spaceStatus?: number
  voyageHours?: number
  nPrice?: number | string
  minPrice?: number | string
  p45?: number | string
  p100?: number | string
  p300?: number | string
  p500?: number | string
  p700?: number | string
  p1000?: number | string
  p2000?: number | string
  /** 空运基础运费矩阵的商务加价派生结果。 */
  businessPrice?: BusinessIncreaseRuleAirPrice
  /** Family手动分享冻结的完整最终价表；普通维护查询可不返回。 */
  publishedPriceItems?: FamilyPublishedPriceItem[] | null
}

export interface PriceAirVO extends PriceAirListVO {
  matrix?: any
  chargingItems?: any[]
  surcharges?: any[]
}

// ==================== 查询条件类型 ====================
export interface FilterCondition {
  fieldName: string
  operator: QueryOperator
  value: any
  sorts?: any
}

export enum QueryOperator {
  EQUAL = 'eq',
  NOT_EQUAL = 'ne',
  GREATER_THAN = 'gt',
  GREATER_EQUAL = 'ge',
  LESS_THAN = 'lt',
  LESS_EQUAL = 'le',
  LIKE = 'like',
  IN = 'in',
  NOT_IN = 'nin',
  BETWEEN = 'between',
  IS_NULL = 'isNull',
  IS_NOT_NULL = 'isNotNull'
}

// ==================== 枚举类型 ====================
export enum FreightType {
  CONTRACT = 1, // 合约价
  SPOT = 2 // SPOT电商
}

export enum CargoType {
  GENERAL = 1, // 普货
  DANGEROUS = 2, // 危险品
  FROZEN = 3, // 冷冻品
  SPECIAL = 4, // 特种箱
  STEEL_COIL = 5, // 卷钢
  LIQUID = 6, // 液体
  CHEMICAL = 7, // 化工品
  TEXTILE = 8 // 纺织品
}

export enum SpaceStatus {
  SMOOTH = 1, // 畅接
  NORMAL = 2, // 正常
  SINGLE_APPLY = 3, // 单票申请
  OVERBOOKED = 4, // 爆舱
  NOT_ACCEPTING = 5 // 不接
}

export enum PriceTrend {
  RISING = 1, // 上涨
  FALLING = 2, // 下跌
  STABLE = 3 // 平稳
}

export enum RateStatus {
  DRAFT = 1, // 草稿
  NORMAL = 2, // 正常
  EXPIRED = 3 // 过期
}

export enum ChargingItemType {
  CONTAINER = 1, // 按箱型计费
  NON_CONTAINER = 2 // 非按箱型计费
}

// ==================== 查询专用类型 ====================
export interface RateQueryOptions {
  includeDeleted?: boolean // 是否包含已删除数据
  includeExpired?: boolean // 是否包含已过期数据
  sortBy?: string // 排序字段
  sortOrder?: 'asc' | 'desc' // 排序方向
  timeRange?: {
    startTime: string
    endTime: string
  }
}

export interface RateQueryResult<T> {
  data: T[]
  total: number
  pageInfo: {
    current: number
    size: number
    pages: number
  }
  queryTime: string
  conditions: FilterCondition[]
}
